import { useQuery } from '@apollo/client';
import cn from 'classnames';
import { useCallback, useMemo, useRef } from 'react';
import Slider from 'react-slick';
// Types
import {
  Gender,
  MemorabiliaFilterInput,
  MemorabiliaOrderBy,
  MemorabiliaStatus,
  MerchProductOrderBy,
  SortDirection,
  StoreStatus,
} from 'api/graphql-global-types';
import { GetAffiliateStreamProducts_getAffiliateStreamProducts } from 'api/streams/types/GetAffiliateStreamProducts';
import {
  GetMerchMinimalInfo,
  GetMerchMinimalInfoVariables,
} from 'api/merch/types/GetMerchMinimalInfo';
// Api
import { GET_MERCH_MINIMAL_INFO } from 'api/merch/queries';
// Hooks
import {
  useGetAthleteMemorabilia,
  useBreakpoints,
  useCarouselArrows,
} from 'hooks';
import { useGetExperience } from 'hooks/useGetExperiences';
// Ui
import Loader from 'ui3/Loader/Loader';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Components
import RelatedMerchItem from 'components/common3/MerchList/components/MerchListItem/MerchListItem';
import RelatedMemorabiliaItem from 'components/common3/MemorabiliaList/components/MemorabiliaListItem';
import RelatedExperienceItem from 'components/common3/ExperienceList/components/ExperienceListItem/ExperienceListItem';
import CarouselSlider from 'components/common3/CarouselSlider/CarouselSlider';
import AffiliateProductItem from 'components/common2/AffiliateProductItem/AffiliateProductItem';
import Section from 'components/common3/Section/Section';
// Styles
import styles from './RelatedProductsList.module.scss';

type RelatedProductsListProps = {
  storeId: string;
  theme?: Theme;
  title?: string;
  backgroundTitle?: string;
  className?: string;
  shouldHideMostExpensiveItem?: boolean;
  isProductDetailsPage?: boolean;
  currentId?: string | number;
  affiliateProducts?: GetAffiliateStreamProducts_getAffiliateStreamProducts[];
  affiliateProductsLoading?: boolean;
  hideArrowCount?: number;
  showOnlyMerch?: boolean;
  showOnlyMemorabilia?: boolean;
  showOnlyExperience?: boolean;
  seeAllLink?: string;
  seeAllLinkTitle?: string;
};

const RelatedProductsList = ({
  storeId,
  theme = 'black',
  title,
  className,
  shouldHideMostExpensiveItem = false,
  currentId,
  isProductDetailsPage,
  affiliateProducts = [],
  affiliateProductsLoading = false,
  showOnlyMerch = false,
  showOnlyMemorabilia = false,
  showOnlyExperience = false,
  seeAllLink = '',
  seeAllLinkTitle = 'View Shop',
}: RelatedProductsListProps) => {
  const carouselRef = useRef<InstanceType<typeof Slider> | null>(null);
  const { isBreakpointL } = useBreakpoints();

  const handlePrev = useCallback(() => {
    carouselRef.current?.slickPrev();
  }, []);

  const handleNext = useCallback(() => {
    carouselRef.current?.slickNext();
  }, []);

  const renderArrowButtons = () => (
    <>
      <button
        type="button"
        onClick={handlePrev}
        disabled={atStart}
        className={styles.arrowButton}
        aria-label="Previous"
      >
        <MillionsIcon name="arrowLeft" className={styles.arrowIcon} />
      </button>
      <button
        type="button"
        onClick={handleNext}
        disabled={atEnd}
        className={styles.arrowButton}
        aria-label="Next"
      >
        <MillionsIcon name="arrowRight" className={styles.arrowIcon} />
      </button>
    </>
  );

  const renderDesktopArrows = () =>
    showDesktopArrows ? (
      <div className={styles.desktopArrows}>{renderArrowButtons()}</div>
    ) : null;

  const renderMobileArrows = () =>
    showMobileArrows ? (
      <div className={styles.mobileArrows}>{renderArrowButtons()}</div>
    ) : null;

  // Create immutable variables object based on props to avoid shared state pollution
  const memorabiliaVariables = useMemo<MemorabiliaFilterInput>(() => {
    const baseVariables: MemorabiliaFilterInput = {
      statuses: [MemorabiliaStatus.Active],
      limit: 8, // no need to fetch more as it's only meant to show several items on a carousel
      // it has to be null in order to show memorabilia even if isOrphanedPage: true (hide my profile in back office)
      isOrphanPage: null,
      orderBy: MemorabiliaOrderBy.createdAt,
      direction: SortDirection.DESC,
    };

    return baseVariables;
  }, []);

  // using useQuery instead of hook to avoid athleteSlug skip for pages without athlete slug
  const { data: merchData, loading: merchLoading } = useQuery<
    GetMerchMinimalInfo,
    GetMerchMinimalInfoVariables
  >(GET_MERCH_MINIMAL_INFO, {
    variables: {
      input: {
        orderBy: MerchProductOrderBy.Date,
        storeIds: [storeId],
        // Product requested to display only adult items
        gender: [Gender.Male, Gender.Female],
        limit: 8,
      },
    },
    skip: showOnlyExperience || showOnlyMemorabilia || !storeId,
  });

  const {
    data: memorabiliaData,
    loading: memorabiliaLoading,
  } = useGetAthleteMemorabilia({
    storeId,
    variables: memorabiliaVariables,
    skip: showOnlyExperience || showOnlyMerch,
  });

  const { data: experienceData, loading: experienceLoading } = useGetExperience(
    {
      variables: {
        input: {
          storeIds: storeId ? [storeId] : [],
          storeStatuses: [StoreStatus.Active],
          limit: 8,
        },
      },
      skip:
        showOnlyMerch ||
        showOnlyMemorabilia ||
        (showOnlyExperience && !storeId),
    }
  );

  const relatedMerch =
    merchData?.getMerchProducts.entities?.filter(
      ({ id }) => id !== currentId
    ) || [];

  const mostExpensiveItemId = memorabiliaData?.getMemorabilia?.entities
    ?.slice()
    .sort((a, b) => (b?.price || 0) - (a?.price || 0))[0]?.id;

  const athleteProfileMemorabilia =
    memorabiliaData?.getMemorabilia.entities?.filter(
      (item) => item.id !== mostExpensiveItemId
    ) || [];

  const memorabiliaDetailsPageData =
    memorabiliaData?.getMemorabilia.entities?.filter(
      (item) => item.id !== currentId
    ) || [];

  const memorabilia = showOnlyMemorabilia
    ? memorabiliaData?.getMemorabilia.entities
    : isProductDetailsPage
    ? memorabiliaDetailsPageData
    : shouldHideMostExpensiveItem
    ? athleteProfileMemorabilia
    : memorabiliaData?.getMemorabilia.entities || [];

  const relatedMemorabilia =
    memorabiliaData?.getMemorabilia?.entities?.filter(({ id }) =>
      isProductDetailsPage ? id !== currentId : true
    ) || [];

  const relatedExperience =
    experienceData?.getExperiences?.entities?.filter(({ id }) => {
      return id !== currentId;
    }) || [];

  const merchLength: number = (relatedMerch && relatedMerch.length) || 0;
  const memorabiliaLength: number =
    (relatedMemorabilia && relatedMemorabilia.length) || 0;
  const experienceLength: number =
    (relatedExperience && relatedExperience.length) || 0;

  const isEmptyList = Boolean(
    !merchLoading &&
      merchData &&
      !merchLength &&
      !memorabiliaLoading &&
      !affiliateProductsLoading &&
      memorabiliaData &&
      !affiliateProducts.length &&
      !memorabiliaLength &&
      experienceData &&
      !experienceLoading &&
      !experienceLength
  );

  const hasRelatedMerch = showOnlyMerch && merchLength;
  const hasRelatedMemorabilia = showOnlyMemorabilia && memorabiliaLength;
  const hasRelatedExperience = showOnlyExperience && experienceLength;

  const shouldReturnNullForFilteredMode =
    (showOnlyExperience && !experienceLoading && experienceLength === 0) ||
    (showOnlyMerch && !merchLoading && merchLength === 0) ||
    (showOnlyMemorabilia && !memorabiliaLoading && memorabiliaLength === 0);

  const carouselItemCount = showOnlyMerch
    ? merchLength
    : showOnlyMemorabilia
    ? memorabilia?.length ?? 0
    : showOnlyExperience
    ? experienceLength
    : merchLength + memorabiliaLength;

  const { canScroll, atStart, atEnd, onBeforeChange } = useCarouselArrows(
    carouselRef,
    carouselItemCount + affiliateProducts.length
  );

  const showDesktopArrows = isBreakpointL && canScroll;
  // Mobile keeps the count-based threshold to match other carousels.
  const showMobileArrows = !isBreakpointL && carouselItemCount >= 3;

  const renderMerch = () => {
    return (
      <Section
        title={title ?? ''}
        className={styles.relatedProductsSection}
        seeAllLink={seeAllLink}
        seeAllLinkTitle={seeAllLinkTitle}
        headerRightContent={renderDesktopArrows()}
      >
        <div className={styles.carouselWithArrows}>
          <div className={styles.rootCarousel}>
            <CarouselSlider
              ref={carouselRef}
              settings={{ beforeChange: onBeforeChange }}
            >
              {affiliateProducts.map((item) => (
                <AffiliateProductItem
                  key={item.id}
                  affiliateProduct={item}
                  theme={theme}
                />
              ))}
              {relatedMerch.map((merch) => (
                <RelatedMerchItem
                  key={merch.id}
                  merch={merch}
                  openInNewTab
                  imageLazyLoading
                  type="default"
                  showBuyNowButton
                />
              ))}
            </CarouselSlider>
          </div>
          {renderMobileArrows()}
        </div>
      </Section>
    );
  };

  const renderMemorabilia = () => {
    return (
      <Section
        title={title ?? ''}
        className={styles.relatedProductsSection}
        seeAllLink={seeAllLink}
        seeAllLinkTitle={seeAllLinkTitle}
        headerRightContent={renderDesktopArrows()}
      >
        <div className={styles.carouselWithArrows}>
          <div className={styles.rootCarousel}>
            <CarouselSlider
              ref={carouselRef}
              settings={{ beforeChange: onBeforeChange }}
            >
              {affiliateProducts.map((item) => (
                <AffiliateProductItem
                  key={item.id}
                  affiliateProduct={item}
                  theme={theme}
                />
              ))}
              {memorabilia?.map((item) => (
                <RelatedMemorabiliaItem
                  key={item.id}
                  memorabilia={item}
                  itemType="default"
                  openInNewTab
                  imageLazyLoading
                  showBuyNowButton
                />
              ))}
            </CarouselSlider>
          </div>
          {renderMobileArrows()}
        </div>
      </Section>
    );
  };

  const renderExperience = () => {
    return (
      <Section
        title={title ?? ''}
        className={styles.relatedProductsSection}
        seeAllLink={seeAllLink}
        seeAllLinkTitle={seeAllLinkTitle}
        headerRightContent={renderDesktopArrows()}
      >
        <div className={styles.carouselWithArrows}>
          <div className={styles.rootCarousel}>
            <CarouselSlider
              ref={carouselRef}
              settings={{ beforeChange: onBeforeChange }}
            >
              {affiliateProducts.map((item) => (
                <AffiliateProductItem
                  key={item.id}
                  affiliateProduct={item}
                  theme={theme}
                />
              ))}
              {relatedExperience.map((experience) => (
                <RelatedExperienceItem
                  key={experience.id}
                  experience={experience}
                  type="default"
                  showBuyNowButton
                />
              ))}
            </CarouselSlider>
          </div>
          {renderMobileArrows()}
        </div>
      </Section>
    );
  };

  // NOTE: renderAll without Experience, as it's a separate class of merchandise
  const renderAll = () => {
    return (
      <Section
        title={title ?? ''}
        className={styles.relatedProductsSection}
        seeAllLink={seeAllLink}
        seeAllLinkTitle={seeAllLinkTitle}
        headerRightContent={renderDesktopArrows()}
      >
        <div className={styles.carouselWithArrows}>
          <div className={styles.rootCarousel}>
            <CarouselSlider
              ref={carouselRef}
              settings={{ beforeChange: onBeforeChange }}
            >
              {affiliateProducts.map((item) => (
                <AffiliateProductItem
                  key={item.id}
                  affiliateProduct={item}
                  theme={theme}
                />
              ))}
              {relatedMemorabilia.map((memorabilia) => (
                <RelatedMemorabiliaItem
                  key={memorabilia.id}
                  memorabilia={memorabilia}
                  itemType="default"
                  openInNewTab
                  imageLazyLoading
                  showBuyNowButton
                />
              ))}
              {relatedMerch.map((merch) => (
                <RelatedMerchItem
                  key={merch.id}
                  merch={merch}
                  openInNewTab
                  imageLazyLoading
                  type="default"
                  showBuyNowButton
                />
              ))}
            </CarouselSlider>
          </div>
          {renderMobileArrows()}
        </div>
      </Section>
    );
  };

  const renderCarousel = () => {
    if (hasRelatedMemorabilia) {
      return renderMemorabilia();
    }
    if (hasRelatedMerch) {
      return renderMerch();
    }
    if (hasRelatedExperience) {
      return renderExperience();
    }
    if (!showOnlyMerch && !showOnlyMemorabilia && !showOnlyExperience) {
      return renderAll();
    }
  };

  if (isEmptyList || shouldReturnNullForFilteredMode) {
    return null;
  }

  return (
    <div className={cn(styles.wrapper, className)}>
      <Loader
        loading={
          merchLoading ||
          memorabiliaLoading ||
          affiliateProductsLoading ||
          experienceLoading
        }
        size="large"
      />
      {renderCarousel()}
    </div>
  );
};

export default RelatedProductsList;
